/**
 * @file The one reading of a fetch job's store state that means "the fetch this table was following
 * no longer exists". Consumers: `SignifyFetchJobModalContent`, which routes to the entry step on it,
 * and `SignifyFetchJobMenuLabel`, which drops the count on it — one predicate because the modal
 * saying a job is gone while the menu still counts it is the contradiction this closes.
 * Two readings qualify. `data === null` is the listener confirming the document is absent. A
 * `permission-denied` is a job reaped from under a live listener: the rules deny a read of a
 * document that is not there, and `persistDocument` leaves `data` frozen at the last snapshot while
 * setting `error`, so that arm has to be read or the count goes on ticking for a job that is gone.
 * Any other listener error is a transient failure on a job that may well still be running, and
 * routing it here would offer Start on a live fetch and mint a second job.
 */

import { FirebaseError } from 'firebase/app';
import type { Snapshot } from 'valtio';
import type { SignifyFetchJob } from '#types';
import type { DocumentState } from '@/store';

export const signifyFetchJobNoLongerExists = (
  job: Pick<Snapshot<DocumentState<SignifyFetchJob>>, 'data' | 'error'>,
): boolean =>
  job.data === null ||
  (job.error instanceof FirebaseError && job.error.code === 'permission-denied');
